import type { Beast, BeastState } from '../../../models/Beast';
import type { Surroundings } from '../../../models/Surroundings';
import type { IBeastState } from './IBeastState';
import { BEAST, DirectionsVectors } from '../../../models/constants';
import { HuntingState } from '../being/HuntingState';
import { BeastExploringState } from './BeastExploringState';
import { bestDirection } from '../scoring';

/**
 * Interrupt state: a Beast flees when threatened. A threat is either lethal terrain
 * (`isInDanger`) or a nearby hunting being. While fleeing it routes away from the
 * herd by passing a negative social weight to `bestDirection`, so its escape steers
 * clear of all nearby beings regardless of their state — the inverse of the social
 * attraction that pulls beings together.
 */
export class BeastFleeingState implements IBeastState {
    static readonly stateName = 'Beast Fleeing';

    get allowInterrupt(): boolean {
        return true;
    }

    canEnter(_beast: Beast, surroundings: Surroundings): boolean {
        return this.isThreatened(surroundings);
    }

    tick(beast: Beast, surroundings: Surroundings): BeastState {
        if (!this.isThreatened(surroundings)) return BeastExploringState.stateName;

        beast.directionTicks = 0;
        beast.moveDirection = bestDirection(beast, surroundings, true, BEAST.FLEE_AVOIDANCE_WEIGHT);
        const vec = DirectionsVectors.get(beast.moveDirection)!;
        beast.translate(vec.dx * BEAST.FLEE_SPEED, vec.dz * BEAST.FLEE_SPEED);

        return BeastFleeingState.stateName;
    }

    /** True when lethal terrain or a hunting being threatens the Beast. */
    private isThreatened(surroundings: Surroundings): boolean {
        if (surroundings.isInDanger) return true;
        return surroundings.nearbyBeings.some(being => being.state === HuntingState.stateName);
    }
}
